import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Activity } from 'lucide-react'

import { type ExplorerTxRecord, api, isAbortError } from '@/shared/api/client'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import ListCard from '@/shared/ui/ListCard'
import TxTable from '@/widgets/tx-table/TxTable'

const POLL_INTERVAL_MS = 10_000
const MANUAL_REFRESH_COOLDOWN_MS = 3_000

const LatestTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<ExplorerTxRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [manualLockedUntil, setManualLockedUntil] = useState(0)
  const [nextRefreshAt, setNextRefreshAt] = useState<number | null>(null)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const isMobile = useIsMobile()

  const cancelledRef = useRef(false)
  const inFlightRef = useRef(false)
  const timerRef = useRef<number | null>(null)
  const ctrlRef = useRef<AbortController | null>(null)
  const fetchRef = useRef<(isManual: boolean) => void>(() => {})

  useEffect(() => {
    cancelledRef.current = false

    const clearTimer = () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    const scheduleNext = () => {
      clearTimer()
      if (cancelledRef.current) return
      setNextRefreshAt(Date.now() + POLL_INTERVAL_MS)
      timerRef.current = window.setTimeout(() => {
        void runFetch(false, false)
      }, POLL_INTERVAL_MS)
    }

    const runFetch = async (showLoading: boolean, isManual: boolean) => {
      if (inFlightRef.current) return
      inFlightRef.current = true
      ctrlRef.current?.abort()
      const ctrl = new AbortController()
      ctrlRef.current = ctrl

      if (showLoading || isManual) setIsFetching(true)
      if (showLoading) setLoading(true)

      try {
        const data = await api.getLatestTransactions({ signal: ctrl.signal })
        if (cancelledRef.current) return
        setTransactions(data)
        setError(null)
      } catch (err) {
        if (cancelledRef.current || isAbortError(err)) return
        const msg = err instanceof Error ? err.message : 'Failed to load latest transactions'
        console.error('Failed to fetch latest transactions:', err)
        setError(msg)
      } finally {
        inFlightRef.current = false
        if (!cancelledRef.current) {
          if (showLoading) setLoading(false)
          if (isManual) setManualLockedUntil(Date.now() + MANUAL_REFRESH_COOLDOWN_MS)
          if (showLoading || isManual) setIsFetching(false)
          scheduleNext()
        }
      }
    }

    fetchRef.current = (isManual: boolean) => {
      void runFetch(false, isManual)
    }

    void runFetch(true, false)

    return () => {
      cancelledRef.current = true
      clearTimer()
      ctrlRef.current?.abort()
      ctrlRef.current = null
      fetchRef.current = () => {}
    }
  }, [])

  useEffect(() => {
    const tickId = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(tickId)
  }, [])

  const cooldownSeconds = Math.max(0, Math.ceil((manualLockedUntil - nowMs) / 1000))
  const refreshCountdownSeconds = Math.min(
    Math.ceil(POLL_INTERVAL_MS / 1000),
    Math.max(0, Math.ceil(((nextRefreshAt ?? nowMs) - nowMs) / 1000)),
  )
  const canManualRefresh = !loading && !isFetching && cooldownSeconds <= 0
  const refreshButtonLabel = isFetching
    ? 'Refreshing...'
    : cooldownSeconds > 0
      ? `Refresh (${cooldownSeconds}s)`
      : 'Refresh'

  const handleManualRefresh = useCallback(() => {
    if (!canManualRefresh) return
    fetchRef.current(true)
  }, [canManualRefresh])

  if (loading) {
    return (
      <div style={{ width: '100%', padding: 'var(--space-40)', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="error-state" style={{ marginTop: 'var(--space-xl)' }} role="alert">
        {error}
      </div>
    )
  }

  if (transactions.length === 0) {
    return (
      <div className="empty-state" style={{ marginTop: 'var(--space-xl)' }}>
        No transactions yet.
      </div>
    )
  }

  return (
    <div
      className="fade-in"
      style={{
        width: '100%',
        marginTop: 'var(--space-3xl)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-xl)',
          padding: '0 var(--space-md)',
          gap: 'var(--space-md)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <Activity size={20} style={{ color: 'var(--brand-primary)' }} />
          <h2
            style={{
              fontSize: isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)',
              fontWeight: 'bold',
              margin: 0,
            }}
          >
            Latest Transactions
          </h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          {!isMobile && (
            <span
              style={{
                color: 'var(--app-text-secondary)',
                fontSize: 'var(--font-size-sm)',
                minWidth: 'var(--latest-tx-hash-width)',
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              Auto refresh: {refreshCountdownSeconds}s
            </span>
          )}
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={!canManualRefresh}
            aria-label="Refresh latest transactions"
            style={{
              border: '1px solid var(--app-divider)',
              borderRadius: 'var(--radius-md)',
              padding: isMobile ? 'var(--space-sm) var(--space-10)' : 'var(--space-sm) var(--space-lg)',
              fontSize: 'var(--font-size-sm)',
              minWidth: isMobile ? 'var(--refresh-btn-mobile-width)' : 'var(--refresh-btn-desktop-width)',
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
              color: canManualRefresh ? 'var(--app-text-primary)' : 'var(--app-text-secondary)',
              backgroundColor: 'transparent',
              cursor: canManualRefresh ? 'pointer' : 'not-allowed',
            }}
          >
            {refreshButtonLabel}
          </button>
        </div>
      </div>

      <ListCard>
        <TxTable rows={transactions} emptyText="No transactions yet." />
      </ListCard>
    </div>
  )
}

export default LatestTransactions
